import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, Stethoscope, Calendar, DollarSign, AlertTriangle, ArrowRight,
  TrendingUp, FlaskConical, Sparkles,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import { ROLE_LABELS } from "@/lib/nav";

const STATUS_BADGE = {
  pending: "bg-muted text-muted-foreground",
  confirmed: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))]",
  checked_in: "bg-accent/20 text-accent-foreground",
  completed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  cancelled: "bg-destructive/15 text-destructive",
  no_show: "bg-destructive/15 text-destructive",
};

function StatCard({ label, value, icon: Icon, hint, accent }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">{label}</div>
            <div className="text-3xl font-bold mt-2 tracking-tight">{value}</div>
            {hint && <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
              <TrendingUp className="size-3" /> {hint}
            </div>}
          </div>
          <div className={`size-10 rounded-md grid place-items-center ${accent || "bg-primary/10 text-primary"}`}>
            <Icon className="size-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, r] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/recent-appointments"),
      ]);
      setStats(s.data);
      setRecent(r.data);
    })();
  }, []);

  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="space-y-6 fade-up" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">{today}</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1.5">
            Good day, {user.name?.split(" ")[0]} 
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Here's what's happening across your clinic today. {ROLE_LABELS[user.role]} view.
          </p>
        </div>
        <Link to="/app/ai">
          <Button className="gap-2" data-testid="open-ai-btn">
            <Sparkles className="size-4" />
            Ask Senrize AI
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Today appts" value={stats?.today_appointments ?? "—"} icon={Calendar} hint="Active queue" />
        <StatCard label="Total patients" value={stats?.total_patients ?? "—"} icon={Users}
          accent="bg-accent/15 text-accent" />
        <StatCard label="Doctors" value={stats?.total_doctors ?? "—"} icon={Stethoscope}
          accent="bg-[hsl(var(--info))]/15 text-[hsl(var(--info))]" />
        <StatCard label="Revenue" value={`$${(stats?.revenue_total ?? 0).toLocaleString()}`} icon={DollarSign}
          accent="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" hint="All-time paid" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <Card className="lg:col-span-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Revenue overview</CardTitle>
              <div className="text-xs text-muted-foreground mt-1">Paid invoices over the last 6 months</div>
            </div>
            <Badge variant="outline" className="font-mono">${stats?.revenue_outstanding?.toLocaleString() ?? "0"} outstanding</Badge>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.revenue_chart || []}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))", border: "1px solid hsl(var(--border))",
                      borderRadius: 8, fontSize: 12,
                    }}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle>Appointment status</CardTitle>
            <div className="text-xs text-muted-foreground mt-1">Current distribution</div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.appointments_by_status || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="status" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip contentStyle={{
                    background: "hsl(var(--card))", border: "1px solid hsl(var(--border))",
                    borderRadius: 8, fontSize: 12,
                  }} />
                  <Bar dataKey="count" radius={[6,6,0,0]}>
                    {(stats?.appointments_by_status || []).map((s, i) => (
                      <Cell key={s.status || i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-12">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent appointments</CardTitle>
              <div className="text-xs text-muted-foreground mt-1">Latest 8 appointments</div>
            </div>
            <Link to="/app/appointments">
              <Button variant="ghost" size="sm" className="gap-1" data-testid="view-all-appts">
                View all <ArrowRight className="size-3.5" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="py-3 pr-4 font-semibold">Patient</th>
                    <th className="py-3 pr-4 font-semibold">Doctor</th>
                    <th className="py-3 pr-4 font-semibold">Date</th>
                    <th className="py-3 pr-4 font-semibold">Reason</th>
                    <th className="py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors">
                      <td className="py-3 pr-4 font-medium">{a.patient_name}</td>
                      <td className="py-3 pr-4 text-muted-foreground">{a.doctor_name}</td>
                      <td className="py-3 pr-4 mono text-xs">{new Date(a.date).toLocaleString()}</td>
                      <td className="py-3 pr-4 text-muted-foreground">{a.reason || "—"}</td>
                      <td className="py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[a.status] || ""}`}>
                          {a.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {recent.length === 0 && (
                    <tr><td colSpan="5" className="py-8 text-center text-muted-foreground">No recent appointments.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
